import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Brain, Cpu, Atom, Scale, Play, BookOpen, User, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const iconMap = {
  brain: Brain,
  cpu: Cpu,
  atom: Atom,
  scale: Scale,
  sparkles: Brain,
  activity: Cpu,
  link: Atom,
  user: User,
};

export const HomePage = () => {
  const [researchThemes, setResearchThemes] = useState([]);
  const [publications, setPublications] = useState([]);
  const [videos, setVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [themesRes, pubsRes, videosRes] = await Promise.all([
          axios.get(`${API}/research-themes`),
          axios.get(`${API}/publications?featured=true`),
          axios.get(`${API}/videos?featured=true`),
        ]);
        setResearchThemes(themesRes.data.slice(0, 4));
        setPublications(pubsRes.data.slice(0, 3));
        setVideos(videosRes.data.slice(0, 3));
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  return (
    <div id="main-content" data-testid="home-page">
      {/* Hero Section */}
      <section
        className="relative min-h-[90vh] flex items-center pt-20 bg-slate-900 overflow-hidden"
        data-testid="hero-section"
      >
        {/* Layered gradient for depth */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950" />
        {/* Radial spotlight */}
        <div
          className="absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(ellipse 80% 50% at 20% 40%, rgba(59,130,246,0.18), transparent 60%), radial-gradient(ellipse 60% 50% at 80% 60%, rgba(139,92,246,0.12), transparent 60%)",
          }}
        />
        {/* Subtle grid lines */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
            backgroundSize: "64px 64px",
          }}
        />
        {/* Grain */}
        <div
          className="absolute inset-0 opacity-[0.06] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.8'/></svg>\")",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 w-full">
          <div className="max-w-4xl">
            <p className="overline text-blue-300 mb-4 animate-fade-in">
              Centre for Consciousness Studies Australia
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight mb-6 animate-slide-up">
              Advancing the Study of Consciousness in an Age of AI and Neurotechnology
            </h1>
            <p className="text-lg sm:text-xl text-slate-300 leading-relaxed mb-8 max-w-3xl animate-slide-up stagger-1">
              The Centre for Consciousness Studies Australia is dedicated to advancing rigorous interdisciplinary research across consciousness studies, neurotechnology, artificial intelligence, fundamental physics, and the deeper scientific and philosophical foundations of mind and reality, with a focus on governance, ethics, safety, and sustainable development.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-slide-up stagger-2">
              <Button
                asChild
                size="lg"
                className="bg-white text-slate-900 hover:bg-slate-100 px-8"
                data-testid="hero-explore-research-btn"
              >
                <Link to="/research">
                  Explore Research <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-slate-500 text-white hover:bg-slate-800 hover:text-white bg-transparent"
                data-testid="hero-about-btn"
              >
                <Link to="/about">About the Centre</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Consciousness Studies Section */}
      <section className="py-16 lg:py-24 bg-white" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="overline text-brand-primary mb-4">Why This Matters</p>
              <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-6">
                Understanding Consciousness at a Critical Moment
              </h2>
              <div className="prose-custom">
                <p>
                  We stand at an unprecedented moment in human history. Rapid advances in artificial intelligence and neurotechnology are transforming our understanding of minds, intelligence, and cognition — while raising profound questions about the nature of consciousness itself.
                </p>
                <p>
                  The Centre for Consciousness Studies Australia brings together leading researchers to address these fundamental questions through rigorous interdisciplinary scholarship, ensuring that humanity navigates this transformation with wisdom, ethics, and foresight.
                </p>
              </div>
              <Button
                asChild
                variant="link"
                className="px-0 text-brand-primary hover:text-brand-accent mt-4"
                data-testid="learn-more-btn"
              >
                <Link to="/impact">
                  Learn about our Impact <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
            <div className="relative">
              <div className="rounded-lg overflow-hidden shadow-lg bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950 relative aspect-[4/3] max-h-[400px]">
                <div
                  className="absolute inset-0 opacity-60"
                  style={{
                    background:
                      "radial-gradient(ellipse 70% 50% at 25% 40%, rgba(59,130,246,0.25), transparent 60%), radial-gradient(ellipse 60% 50% at 80% 70%, rgba(139,92,246,0.18), transparent 60%)",
                  }}
                />
                <div
                  className="absolute inset-0 opacity-[0.08]"
                  style={{
                    backgroundImage:
                      "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
                    backgroundSize: "48px 48px",
                  }}
                />
                <div className="relative h-full w-full flex items-center justify-center p-10 text-center">
                  <div>
                    <div className="flex gap-4 justify-center mb-5">
                      <Brain className="w-10 h-10 text-blue-300" strokeWidth={1.25} />
                      <Cpu className="w-10 h-10 text-blue-300" strokeWidth={1.25} />
                      <Atom className="w-10 h-10 text-blue-300" strokeWidth={1.25} />
                    </div>
                    <p className="font-serif text-xl text-white leading-snug max-w-xs mx-auto">
                      Advancing understanding at the intersection of mind, technology, and reality.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Research Themes Section */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="research-themes-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="overline text-brand-primary mb-4">Research Portfolio</p>
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-4">
              Key Research Themes
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our interdisciplinary research spans the foundational questions of consciousness, mind, intelligence, and the technologies shaping our future.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {researchThemes.map((theme, index) => {
              const IconComponent = iconMap[theme.icon] || Brain;
              return (
                <Link
                  key={theme.id}
                  to="/research"
                  className="block"
                  data-testid={`research-theme-card-${index}`}
                >
                  <Card className="research-card group cursor-pointer h-full">
                    <CardContent className="p-6">
                      <div className="w-12 h-12 rounded-lg bg-brand-highlight flex items-center justify-center mb-4 group-hover:bg-brand-primary transition-colors">
                        <IconComponent className="w-6 h-6 text-brand-primary group-hover:text-white transition-colors" />
                      </div>
                      <h3 className="font-serif text-lg text-slate-900 mb-2">
                        {theme.title}
                      </h3>
                      <p className="text-sm text-slate-600 line-clamp-3">
                        {theme.description}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Button
              asChild
              variant="outline"
              className="border-slate-300 hover:bg-slate-100"
              data-testid="view-all-research-btn"
            >
              <Link to="/research">
                View All Research Themes <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Publications Section */}
      <section className="py-16 lg:py-24 bg-white" data-testid="publications-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
            <div>
              <p className="overline text-brand-primary mb-2">Research Outputs</p>
              <h2 className="font-serif text-3xl lg:text-4xl text-slate-900">
                Featured Publications
              </h2>
            </div>
            <Button
              asChild
              variant="outline"
              className="border-slate-300"
              data-testid="view-all-publications-btn"
            >
              <Link to="/publications">
                View All <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {publications.map((pub, index) => {
              const hasLink = pub.link && pub.link !== "#";
              const CardBody = (
                <Card className="publication-card h-full group">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="tag tag-primary font-mono text-xs">
                        {pub.category.replace('_', ' ')}
                      </span>
                      <span className="tag font-mono text-xs">{pub.year}</span>
                    </div>
                    <h3 className="font-serif text-lg text-slate-900 mb-3 line-clamp-2 group-hover:text-brand-primary transition-colors">
                      {pub.title}
                    </h3>
                    {pub.authors && pub.authors.length > 0 && (
                      <p className="text-sm text-slate-500 mb-3">
                        {pub.authors.join(", ")}
                      </p>
                    )}
                    <p className="text-sm text-slate-600 line-clamp-3 mb-4">
                      {pub.abstract}
                    </p>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap gap-2">
                        {pub.tags.slice(0, 3).map((tag) => (
                          <span key={tag} className="tag text-xs">
                            {tag}
                          </span>
                        ))}
                      </div>
                      {hasLink && (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary">
                          Read <ExternalLink className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );

              return hasLink ? (
                <a
                  key={pub.id}
                  href={pub.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  data-testid={`publication-card-${index}`}
                >
                  {CardBody}
                </a>
              ) : (
                <div
                  key={pub.id}
                  data-testid={`publication-card-${index}`}
                >
                  {CardBody}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Videos Section */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="videos-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-12">
            <div>
              <p className="overline text-brand-primary mb-2">Watch & Learn</p>
              <h2 className="font-serif text-3xl lg:text-4xl text-slate-900">
                Research Introductions
              </h2>
            </div>
            <Button
              asChild
              variant="outline"
              className="border-slate-300"
              data-testid="view-all-videos-btn"
            >
              <Link to="/publications#videos">
                View All <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video, index) => (
              <Card
                key={video.id}
                className="video-card overflow-hidden cursor-pointer"
                onClick={() => setSelectedVideo(video)}
                data-testid={`video-card-${index}`}
              >
                <div className="relative aspect-video bg-slate-200 group">
                  <img
                    src={video.thumbnail_url}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                      <Play className="w-8 h-8 text-slate-900 ml-1" />
                    </div>
                  </div>
                  {video.duration && (
                    <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded font-mono">
                      {video.duration}
                    </span>
                  )}
                </div>
                <CardContent className="p-4">
                  <span className="tag text-xs mb-2 inline-block">
                    {video.category.replace('_', ' ')}
                  </span>
                  <h3 className="font-serif text-lg text-slate-900 mb-2 line-clamp-2">
                    {video.title}
                  </h3>
                  <p className="text-sm text-slate-600 line-clamp-2">
                    {video.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Video Modal (shared by home videos section) */}
        <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
          <DialogContent className="max-w-4xl p-0 overflow-hidden" data-testid="home-video-modal">
            <DialogHeader className="p-4 pb-0">
              <DialogTitle className="font-serif text-xl">
                {selectedVideo?.title}
              </DialogTitle>
            </DialogHeader>
            <div className="aspect-video bg-black">
              {selectedVideo && (
                <iframe
                  src={selectedVideo.video_url}
                  title={selectedVideo.title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}
            </div>
            <div className="p-4">
              <p className="text-slate-600">{selectedVideo?.description}</p>
            </div>
          </DialogContent>
        </Dialog>
      </section>

      {/* Impact Section */}
      <section className="py-16 lg:py-24 bg-slate-900 text-white" data-testid="transformation-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="overline text-blue-300 mb-4">Strategic Foresight</p>
              <h2 className="font-serif text-3xl lg:text-4xl text-white mb-6">
                A Global Transformation is Underway
              </h2>
              <p className="text-slate-300 leading-relaxed mb-6">
                The convergence of neurotechnology, artificial intelligence, consciousness studies, and fundamental physics is reshaping our understanding of mind, intelligence, and reality itself. This transformation carries profound implications for research, policy, ethics, and the future of humanity.
              </p>
              <p className="text-slate-300 leading-relaxed mb-8">
                The Centre for Consciousness Studies Australia positions itself at this critical intersection, contributing rigorous research and thoughtful policy engagement to ensure these transformative technologies serve humanity's highest potential.
              </p>
              <Button
                asChild
                className="bg-white text-slate-900 hover:bg-slate-100"
                data-testid="explore-transformation-btn"
              >
                <Link to="/impact">
                  Explore Our Impact <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-6 bg-slate-800 rounded-lg">
                <Brain className="w-8 h-8 text-blue-400 mb-3" />
                <h3 className="font-serif text-lg mb-2">Consciousness</h3>
                <p className="text-sm text-slate-400">Understanding the nature of subjective experience</p>
              </div>
              <div className="p-6 bg-slate-800 rounded-lg">
                <Cpu className="w-8 h-8 text-blue-400 mb-3" />
                <h3 className="font-serif text-lg mb-2">AI Systems</h3>
                <p className="text-sm text-slate-400">Machine cognition and artificial minds</p>
              </div>
              <div className="p-6 bg-slate-800 rounded-lg">
                <Atom className="w-8 h-8 text-blue-400 mb-3" />
                <h3 className="font-serif text-lg mb-2">Physics</h3>
                <p className="text-sm text-slate-400">Fundamental nature of reality and mind</p>
              </div>
              <div className="p-6 bg-slate-800 rounded-lg">
                <Scale className="w-8 h-8 text-blue-400 mb-3" />
                <h3 className="font-serif text-lg mb-2">Ethics</h3>
                <p className="text-sm text-slate-400">Governance and responsible innovation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-12 text-center">
            <h2 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-4">
              Engage with Our Research
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto mb-8">
              Whether you're a researcher, policymaker, student, or industry leader — we welcome collaboration and dialogue. Contact us to learn more about our work or explore partnership opportunities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-slate-900 text-white hover:bg-slate-800 px-8"
                data-testid="cta-contact-btn"
              >
                <Link to="/contact">
                  Contact the Centre <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-slate-300"
                data-testid="cta-contributions-btn"
              >
                <Link to="/publications">
                  <BookOpen className="mr-2 w-4 h-4" /> Research Contributions
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
